<template>
  <div :class="$style.chart">
    <div :class="$style.header">
      <h3
        :class="$style.heading"
      >
        <slot name="heading" />
      </h3>

      <div :class="$style.controls">
        <BaseDropdown
          v-if="$slots.actions"
          ref="actionsDropdown"
          placement="bottom-end"
          :offset="[9, 7]"
          @show="onShowActions"
          @hide="onHideActions"
        >
          <template #trigger="{ isActive }">
            <button
              ref="actionsButton"
              :class="[
                $style.actionsButton,
                {
                  [$style.isActive]: isActive,
                },
              ]"
            >
              <DotsMoreSvg />
            </button>
          </template>

          <template #content="{ tippy }">
            <slot
              name="actions"
              :tippy="tippy"
            />
          </template>
        </BaseDropdown>
      </div>
    </div>
    <div
      :class="$style.body"
      :style="{ paddingTop: `${100 / aspectRatio}%` }"
    >
      <div :class="$style.content">
        <h4 :class="$style.value">
          {{ value.toLocaleString() }}
        </h4>
        <h5
          :class="[
            $style.cagr,
            {
              [$style.isZero]: cagr === 0,
              [$style.isNegative]: cagr < 0,
            }
          ]"
        >
          {{ (Math.abs(cagr) * 100).toFixed(2) }}%
        </h5>
      </div>
    </div>
  </div>
</template>

<script>
import BaseDropdown from '@/components/base/Dropdown';
import DotsMoreSvg from '@/assets/images/icons/dots-more.svg?inline';

export default {
  components: {
    BaseDropdown,
    DotsMoreSvg,
  },
  props: {
    datasets: {
      type: Array,
      default: () => [],
    },
    aspectRatio: {
      type: Number,
      default: 1,
    },
  },
  emits: {
    showActions: null,
    hideActions: null,
  },
  computed: {
    value() {
      const data = this.datasets[0]?.data;
      return data?.length ? data[data.length - 1].y.value : 0;
    },
    cagr() {
      const data = this.datasets[0]?.data;

      if (!data?.length) {
        return 0;
      }

      const first = data[0];
      const last = data[data.length - 1];

      let result = null;

      if (first.y.value !== 0) {
        result = ((last.y.value / first.y.value) ** (1 / (last.x - first.x)) - 1).toFixed(4);
      }

      // eslint-disable-next-line no-restricted-globals
      return isNaN(result) ? 0 : result;
    },
  },
  methods: {
    onShowActions() {
      this.$emit('showActions');
    },
    onHideActions() {
      this.$emit('hideActions');
    },
    showActions() {
      this.$refs.actionsDropdown.show();
    },
    hideActions() {
      this.$refs.actionsDropdown.hide();
    },
  },
};
</script>

<style lang="scss" module>
.header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding-bottom: 5px;
  border-bottom: 1px solid #f0f1f2;
}

.heading {
  font-size: 18px;
  font-weight: 500;
  color: $title;
}

.controls {
  display: inline-flex;
  align-items: center;
  margin-left: 10px;
}

.actionsButton {
  @include reset;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  margin-left: 10px;
  overflow: hidden;
  color: $text;
  cursor: pointer;
  background: rgba($disabled, 0);
  border-radius: 50%;
  transition: background 0.2s ease-in-out;

  &:hover,
  &.isActive {
    background: rgba($disabled, 0.25);
  }
}

.body {
  position: relative;
}

.content {
  position: absolute;
  top: 50%;
  left: 0;
  transform: translateY(calc(-50% - 30px));
}

.value {
  padding: 0;
  margin: 0;
  font-size: 65px;
  font-weight: 500;
  color: #424a4e;
}

.cagr {
  padding: 0;
  margin: 0;
  font-size: 45px;
  font-weight: 600;
  color: #58d067;

  &.isNegative {
    color: #fe4485;
  }

  &.isZero {
    color: rgba($text, 0.75);
  }

  &:not(.isZero) {

    &::before {
      display: inline-block;
      width: 0;
      height: 0;
      margin-right: 10px;
      content: '';
      border-color: transparent transparent #58d067;
      border-style: solid;
      border-width: 0 17.5px 30.3px;
    }

    &.isNegative {

      &::before {
        border-color: #fe4485 transparent transparent;
        border-width: 30.3px 17.5px 0;
      }
    }
  }
}
</style>
